import { useState, useEffect, useCallback } from 'react';
import { activitiesApi, activityTypesApi, locationsApi, machineryApi, dashboardApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Plus, Activity, MapPin, Trash2, Cloud, Thermometer, Droplets, Wind, Undo, X, Upload, Image, Calendar, Wrench, Eye } from 'lucide-react';
import Map, { Source, Layer, NavigationControl, Marker } from 'react-map-gl/maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';

// Map styles
const MAP_STYLES = {
  satellite: {
    version: 8,
    sources: {
      'esri-satellite': {
        type: 'raster',
        tiles: ['https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'],
        tileSize: 256,
        attribution: '&copy; Esri'
      }
    },
    layers: [{ id: 'esri-satellite', type: 'raster', source: 'esri-satellite', minzoom: 0, maxzoom: 19 }]
  },
  street: 'https://basemaps.cartocdn.com/gl/positron-gl-style/style.json',
};

const FARM_CENTER = { longitude: 153.137, latitude: -27.943, zoom: 15 };

export function ActivitiesPage() {
  const [activities, setActivities] = useState([]);
  const [activityTypes, setActivityTypes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [machinery, setMachinery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [drawMode, setDrawMode] = useState('point'); // 'point' or 'area'
  const [areaPoints, setAreaPoints] = useState([]);
  const [weather, setWeather] = useState(null);
  const [mapStyle, setMapStyle] = useState('satellite');
  const [filterType, setFilterType] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [photoFiles, setPhotoFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    activity_type_id: '',
    location_id: '',
    date: new Date().toISOString().split('T')[0],
    machinery_id: '',
    notes: '',
  });

  const [viewState, setViewState] = useState({
    longitude: FARM_CENTER.longitude,
    latitude: FARM_CENTER.latitude,
    zoom: FARM_CENTER.zoom,
  });

  const loadData = useCallback(async () => {
    try {
      const [activitiesRes, typesRes, locsRes, machRes] = await Promise.all([
        activitiesApi.getAll({}),
        activityTypesApi.getAll(),
        locationsApi.getAll(),
        machineryApi.getAll(),
      ]);
      setActivities(activitiesRes.data);
      setActivityTypes(typesRes.data);
      setLocations(locsRes.data);
      setMachinery(machRes.data);
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadWeather = useCallback(async () => {
    try {
      const res = await dashboardApi.getWeather(FARM_CENTER.latitude, FARM_CENTER.longitude);
      setWeather(res.data);
    } catch (err) {
      // Weather is non-critical, fail silently in production
    }
  }, []);

  useEffect(() => {
    loadData();
    loadWeather();
  }, [loadData, loadWeather]);

  const handleMapClick = useCallback((e) => {
    const point = [e.lngLat.lng, e.lngLat.lat];
    if (drawMode === 'point') {
      setAreaPoints([point]);
    } else {
      setAreaPoints(prev => [...prev, point]);
    }
  }, [drawMode, setAreaPoints]);

  const undoLastPoint = () => {
    setAreaPoints(prev => prev.slice(0, -1));
  };

  const clearPoints = () => {
    setAreaPoints([]);
  };

  const calculateAreaSqm = (points) => {
    if (points.length < 3) return null;
    // Shoelace formula for polygon area (approximation)
    let area = 0;
    const n = points.length;
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n;
      area += points[i][0] * points[j][1];
      area -= points[j][0] * points[i][1];
    }
    area = Math.abs(area) / 2;
    // Convert from degrees to approximate sqm (at this latitude)
    const metersPerDegree = 111320 * Math.cos(FARM_CENTER.latitude * Math.PI / 180);
    return Math.round(area * metersPerDegree * metersPerDegree);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (areaPoints.length === 0) {
      toast.error('Please mark at least one point on the map');
      return;
    }
    if (!formData.activity_type_id) {
      toast.error('Please select an activity type');
      return;
    }

    try {
      const areaSqm = drawMode === 'area' ? calculateAreaSqm(areaPoints) : null;
      const res = await activitiesApi.create({
        ...formData,
        location_id: formData.location_id || null,
        machinery_id: formData.machinery_id || null,
        area_points: areaPoints,
        area_sqm: areaSqm,
        weather: weather ? {
          conditions: weather.weather?.[0]?.main || 'Unknown',
          temp: weather.main?.temp,
          humidity: weather.main?.humidity,
          wind_speed: weather.wind?.speed,
        } : null,
      });

      // Upload photos if selected
      if (photoFiles.length > 0) {
        setUploading(true);
        const activityId = res.data.id;
        for (const file of photoFiles) {
          const fd = new FormData();
          fd.append('file', file);
          try {
            await activitiesApi.uploadPhoto(activityId, fd);
          } catch {
            toast.error(`Failed to upload ${file.name}`);
          }
        }
        setUploading(false);
      }

      toast.success('Activity recorded successfully');
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (err) {
      toast.error('Failed to record activity');
    }
  };

  const resetForm = () => {
    setFormData({
      activity_type_id: '',
      location_id: '',
      date: new Date().toISOString().split('T')[0],
      machinery_id: '',
      notes: '',
    });
    setAreaPoints([]);
    setDrawMode('point');
    setPhotoFiles([]);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this activity?')) return;
    try {
      await activitiesApi.delete(id);
      toast.success('Activity deleted');
      loadData();
    } catch (err) {
      toast.error('Failed to delete activity');
    }
  };

  const openNewActivity = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openDetail = (activity) => {
    setSelectedActivity(activity);
    setDetailOpen(true);
  };

  const getDetailViewState = (activity) => {
    const pts = activity.area_points || [];
    if (pts.length === 0) return { longitude: FARM_CENTER.longitude, latitude: FARM_CENTER.latitude, zoom: FARM_CENTER.zoom };
    const lngs = pts.map(p => p[0]);
    const lats = pts.map(p => p[1]);
    return {
      longitude: (Math.min(...lngs) + Math.max(...lngs)) / 2,
      latitude: (Math.min(...lats) + Math.max(...lats)) / 2,
      zoom: 16,
    };
  };

  // Find the linked location's boundary for detail map
  const getLocationBoundary = (locationId) => {
    const loc = locations.find(l => l.id === locationId);
    return loc?.coordinates?.length >= 3 ? loc.coordinates : null;
  };

  const filteredActivities = activities.filter(a => {
    if (filterType !== 'all' && a.activity_type_id !== filterType) return false;
    if (startDate && a.date < startDate) return false;
    if (endDate && a.date > endDate) return false;
    return true;
  });

  const areaLineLayer = {
    id: 'area-line',
    type: 'line',
    paint: { 'line-color': '#E07A5F', 'line-width': 3 },
  };

  const areaFillLayer = {
    id: 'area-fill',
    type: 'fill',
    paint: { 'fill-color': '#E07A5F', 'fill-opacity': 0.2 },
  };

  if (loading) {
    return <div className="animate-pulse h-96 bg-muted rounded-md" data-testid="activities-loading" />;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="activities-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground">Activities</h1>
          <p className="text-muted-foreground">Track all farm activities: mowing, spraying, fertilizing & more</p>
        </div>
        <Button className="btn-primary" onClick={openNewActivity} data-testid="add-activity-btn">
          <Plus className="h-4 w-4 mr-2" /> Record Activity
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-2">
              <Label>Activity Type</Label>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-[180px]" data-testid="filter-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {activityTypes.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      <span className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full" style={{ backgroundColor: t.color }} />
                        {t.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Start Date</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>End Date</Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
            <Button variant="outline" onClick={() => { setFilterType('all'); setStartDate(''); setEndDate(''); }}>
              Clear Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Activities Table */}
      <Card data-testid="activities-table">
        <CardHeader>
          <CardTitle className="font-heading text-xl flex items-center gap-2">
            <Activity className="h-5 w-5 text-primary" />
            Activity Log ({filteredActivities.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredActivities.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">No activities recorded yet</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Activity</TableHead>
                  <TableHead>Field</TableHead>
                  <TableHead>Area</TableHead>
                  <TableHead>Equipment</TableHead>
                  <TableHead>Weather</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredActivities.map((activity) => (
                  <TableRow 
                    key={activity.id} 
                    className="table-row-hover cursor-pointer"
                    onClick={() => openDetail(activity)}
                    data-testid={`activity-row-${activity.id}`}
                  >
                    <TableCell>{activity.date}</TableCell>
                    <TableCell>
                      <Badge style={{ backgroundColor: activity.activity_type_color, color: 'white' }}>
                        {activity.activity_type_name}
                      </Badge>
                    </TableCell>
                    <TableCell>{activity.location_name || '-'}</TableCell>
                    <TableCell>
                      {activity.area_sqm ? `${(activity.area_sqm / 10000).toFixed(2)} ha` : `${activity.area_points?.length || 0} pts`}
                    </TableCell>
                    <TableCell>{activity.machinery_name || '-'}</TableCell>
                    <TableCell>
                      {activity.weather ? (
                        <span className="text-sm">{activity.weather.temp}°C, {activity.weather.conditions}</span>
                      ) : '-'}
                    </TableCell>
                    <TableCell className="max-w-[150px] truncate">{activity.notes || '-'}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); openDetail(activity); }} data-testid={`view-activity-${activity.id}`}>
                          <Eye className="h-4 w-4 text-muted-foreground" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); handleDelete(activity.id); }}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add Activity Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[95vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Record Activity</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column - Form */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Activity Type *</Label>
                  <Select 
                    value={formData.activity_type_id} 
                    onValueChange={(v) => setFormData({...formData, activity_type_id: v})}
                  >
                    <SelectTrigger data-testid="activity-type-select">
                      <SelectValue placeholder="Select activity type" />
                    </SelectTrigger>
                    <SelectContent>
                      {activityTypes.map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          <span className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full" style={{ backgroundColor: t.color }} />
                            {t.name}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Field (Optional)</Label>
                  <Select 
                    value={formData.location_id || "none"} 
                    onValueChange={(v) => setFormData({...formData, location_id: v === "none" ? '' : v})}
                  >
                    <SelectTrigger data-testid="activity-location-select">
                      <SelectValue placeholder="Select field" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No specific field</SelectItem>
                      {locations.map((l) => (
                        <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Date *</Label>
                  <Input 
                    type="date" 
                    value={formData.date}
                    onChange={(e) => setFormData({...formData, date: e.target.value})}
                    required
                    data-testid="activity-date-input"
                  />
                </div>

                {/* Weather Widget */}
                {weather && (
                  <Card className="bg-secondary/30">
                    <CardContent className="pt-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Cloud className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm font-medium">Weather at Location</span>
                      </div>
                      <div className="grid grid-cols-4 gap-2 text-sm">
                        <div>
                          <p className="text-muted-foreground">Conditions</p>
                          <p className="font-medium">{weather.weather?.[0]?.main || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Temp</p>
                          <p className="font-medium">{weather.main?.temp}°C</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Humidity</p>
                          <p className="font-medium">{weather.main?.humidity}%</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Wind</p>
                          <p className="font-medium">{weather.wind?.speed} m/s</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                <div className="space-y-2">
                  <Label>Equipment Used (Optional)</Label>
                  <Select 
                    value={formData.machinery_id || "none"} 
                    onValueChange={(v) => setFormData({...formData, machinery_id: v === "none" ? '' : v})}
                  >
                    <SelectTrigger data-testid="activity-machinery-select">
                      <SelectValue placeholder="Select equipment" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No equipment</SelectItem>
                      {machinery.map((m) => (
                        <SelectItem key={m.id} value={m.id}>{m.name} ({m.type})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea 
                    placeholder="Add any notes about this activity..."
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    data-testid="activity-notes-input"
                  />
                </div>

                {/* Photo Upload */}
                <div className="space-y-2">
                  <Label>Photos</Label>
                  <div className="border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:bg-secondary/30 transition-colors" onClick={() => document.getElementById('photo-input').click()}>
                    <input 
                      id="photo-input" 
                      type="file" 
                      accept="image/*" 
                      multiple 
                      className="hidden"
                      onChange={(e) => setPhotoFiles(prev => [...prev, ...Array.from(e.target.files)])}
                      data-testid="photo-file-input"
                    />
                    <Upload className="h-6 w-6 mx-auto text-muted-foreground mb-1" />
                    <p className="text-sm text-muted-foreground">Click to add photos</p>
                  </div>
                  {photoFiles.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {photoFiles.map((file, idx) => (
                        <div key={`photo-${file.name}-${idx}`} className="relative group">
                          <img src={URL.createObjectURL(file)} alt="" className="w-16 h-16 rounded-md object-cover border" />
                          <button 
                            type="button"
                            className="absolute -top-1 -right-1 w-5 h-5 bg-destructive text-white rounded-full flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={() => setPhotoFiles(prev => prev.filter((_, i) => i !== idx))}
                          >
                            <X className="h-3 w-3" />
                          </button>
                          <p className="text-[10px] text-muted-foreground truncate w-16">{file.name}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Right Column - Map */}
              <div className="space-y-3">
                <div>
                  <Label>Location * (Click to add points)</Label>
                  <div className="flex items-center gap-2 mt-2">
                    <Button
                      type="button"
                      size="sm"
                      variant={drawMode === 'point' ? 'default' : 'outline'}
                      onClick={() => { setDrawMode('point'); setAreaPoints([]); }}
                    >
                      <MapPin className="h-4 w-4 mr-1" /> Point
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant={drawMode === 'area' ? 'default' : 'outline'}
                      onClick={() => { setDrawMode('area'); setAreaPoints([]); }}
                      data-testid="draw-area-btn"
                    >
                      Draw Area
                    </Button>
                    <span className="text-sm text-muted-foreground">{areaPoints.length} points</span>
                    {areaPoints.length > 0 && (
                      <>
                        <Button type="button" size="sm" variant="ghost" onClick={undoLastPoint}>
                          <Undo className="h-4 w-4" />
                        </Button>
                        <Button type="button" size="sm" variant="ghost" onClick={clearPoints}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>

                <div className="h-[400px] rounded-md overflow-hidden border relative">
                  <div className="absolute top-2 left-2 z-10 flex gap-1">
                    <Button
                      type="button"
                      size="sm"
                      variant={mapStyle === 'satellite' ? 'default' : 'outline'}
                      onClick={() => setMapStyle('satellite')}
                      className="text-xs h-7"
                    >
                      Satellite
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant={mapStyle === 'street' ? 'default' : 'outline'}
                      onClick={() => setMapStyle('street')}
                      className="text-xs h-7"
                    >
                      Street
                    </Button>
                  </div>
                  <Map
                    {...viewState}
                    onMove={(e) => setViewState(e.viewState)}
                    onClick={handleMapClick}
                    mapStyle={MAP_STYLES[mapStyle]}
                    style={{ width: '100%', height: '100%' }}
                    cursor="crosshair"
                  >
                    <NavigationControl position="top-right" />

                    {/* Area markers */}
                    {areaPoints.map((point, idx) => (
                      <Marker key={`area-pt-${point[0]}-${point[1]}`} longitude={point[0]} latitude={point[1]}>
                        <div className="w-6 h-6 rounded-full bg-accent border-2 border-white shadow-lg flex items-center justify-center text-white text-xs font-bold">
                          {idx + 1}
                        </div>
                      </Marker>
                    ))}

                    {/* Area polygon */}
                    {drawMode === 'area' && areaPoints.length >= 2 && (
                      <Source
                        id="area-source"
                        type="geojson"
                        data={{
                          type: 'Feature',
                          geometry: {
                            type: areaPoints.length >= 3 ? 'Polygon' : 'LineString',
                            coordinates: areaPoints.length >= 3 ? [[...areaPoints, areaPoints[0]]] : areaPoints,
                          },
                        }}
                      >
                        {areaPoints.length >= 3 && <Layer {...areaFillLayer} />}
                        <Layer {...areaLineLayer} />
                      </Source>
                    )}
                  </Map>
                </div>

                {/* Area calculation */}
                {drawMode === 'area' && areaPoints.length >= 3 && (
                  <div className="p-3 bg-secondary/50 rounded-md">
                    <p className="text-sm">
                      <strong>{areaPoints.length} corners</strong> | 
                      <strong> {(calculateAreaSqm(areaPoints) / 10000).toFixed(2)} ha</strong> ({calculateAreaSqm(areaPoints).toLocaleString()} m²)
                    </p>
                    <p className="text-xs text-muted-foreground">Drag corners to adjust</p>
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-2 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" className="flex-1 btn-primary" data-testid="activity-submit-btn" disabled={uploading}>
                {uploading ? 'Uploading photos...' : 'Record Activity'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Activity Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-4xl max-h-[95vh] overflow-y-auto" data-testid="activity-detail-dialog">
          {selectedActivity && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-xl flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: selectedActivity.activity_type_color || '#1B4332' }}>
                    <Activity className="h-4 w-4 text-white" />
                  </div>
                  {selectedActivity.activity_type_name} - {selectedActivity.date}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-5">
                {/* Info Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Activity</p>
                    <Badge style={{ backgroundColor: selectedActivity.activity_type_color, color: 'white' }} data-testid="detail-activity-type">
                      {selectedActivity.activity_type_name}
                    </Badge>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide flex items-center gap-1"><Calendar className="h-3 w-3" /> Date</p>
                    <p className="font-medium" data-testid="detail-date">{selectedActivity.date}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide flex items-center gap-1"><MapPin className="h-3 w-3" /> Field</p>
                    <p className="font-medium" data-testid="detail-field">{selectedActivity.location_name || 'No field'}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide flex items-center gap-1"><Wrench className="h-3 w-3" /> Equipment</p>
                    <p className="font-medium" data-testid="detail-equipment">{selectedActivity.machinery_name || 'None'}</p>
                  </div>
                </div>

                {/* Area info */}
                {selectedActivity.area_sqm && (
                  <div className="p-3 rounded-lg bg-secondary/30 border">
                    <p className="text-sm">
                      <strong>Area covered:</strong> {(selectedActivity.area_sqm / 10000).toFixed(2)} hectares ({selectedActivity.area_sqm.toLocaleString()} m²) 
                      <span className="text-muted-foreground ml-2">({selectedActivity.area_points?.length || 0} boundary points)</span>
                    </p>
                  </div>
                )}

                {/* Weather */}
                {selectedActivity.weather && (
                  <Card className="bg-secondary/20">
                    <CardContent className="pt-4 pb-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Cloud className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm font-medium">Weather Conditions at Time of Activity</span>
                      </div>
                      <div className="grid grid-cols-4 gap-3 text-sm">
                        <div>
                          <p className="text-muted-foreground">Conditions</p>
                          <p className="font-medium">{selectedActivity.weather.conditions}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground flex items-center gap-1"><Thermometer className="h-3 w-3" /> Temp</p>
                          <p className="font-medium">{selectedActivity.weather.temp}°C</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground flex items-center gap-1"><Droplets className="h-3 w-3" /> Humidity</p>
                          <p className="font-medium">{selectedActivity.weather.humidity}%</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground flex items-center gap-1"><Wind className="h-3 w-3" /> Wind</p>
                          <p className="font-medium">{selectedActivity.weather.wind_speed} m/s</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Notes */}
                {selectedActivity.notes && (
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Notes</p>
                    <p className="text-foreground bg-secondary/20 p-3 rounded-lg border" data-testid="detail-notes">{selectedActivity.notes}</p>
                  </div>
                )}

                {/* Photos */}
                {selectedActivity.photos?.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide flex items-center gap-1"><Image className="h-3 w-3" /> Photos ({selectedActivity.photos.length})</p>
                    <div className="flex flex-wrap gap-3" data-testid="detail-photos">
                      {selectedActivity.photos.map((photo, idx) => (
                        <a key={`act-photo-${photo}`} href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${photo}`} target="_blank" rel="noopener noreferrer">
                          <img 
                            src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${photo}`} 
                            alt={`Activity photo ${idx + 1}`} 
                            className="w-24 h-24 rounded-lg object-cover border hover:opacity-80 transition-opacity cursor-pointer"
                          />
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* Map showing activity area */}
                {selectedActivity.area_points?.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Activity Location Map</p>
                    <div className="h-[350px] rounded-lg overflow-hidden border" data-testid="detail-map">
                      <Map
                        initialViewState={getDetailViewState(selectedActivity)}
                        mapStyle={MAP_STYLES.satellite}
                        style={{ width: '100%', height: '100%' }}
                      >
                        <NavigationControl position="top-right" />

                        {/* Field boundary (if linked to a location) */}
                        {selectedActivity.location_id && getLocationBoundary(selectedActivity.location_id) && (
                          <Source
                            id="field-boundary"
                            type="geojson"
                            data={{
                              type: 'Feature',
                              geometry: {
                                type: 'Polygon',
                                coordinates: [
                                  [...getLocationBoundary(selectedActivity.location_id), getLocationBoundary(selectedActivity.location_id)[0]]
                                ],
                              },
                            }}
                          >
                            <Layer id="field-boundary-fill" type="fill" paint={{ 'fill-color': '#1B4332', 'fill-opacity': 0.15 }} />
                            <Layer id="field-boundary-line" type="line" paint={{ 'line-color': '#1B4332', 'line-width': 2, 'line-dasharray': [3, 2] }} />
                          </Source>
                        )}

                        {/* Activity area highlight */}
                        {selectedActivity.area_points.length >= 3 ? (
                          <Source
                            id="activity-area"
                            type="geojson"
                            data={{
                              type: 'Feature',
                              geometry: {
                                type: 'Polygon',
                                coordinates: [[...selectedActivity.area_points, selectedActivity.area_points[0]]],
                              },
                            }}
                          >
                            <Layer id="activity-fill" type="fill" paint={{ 'fill-color': selectedActivity.activity_type_color || '#E07A5F', 'fill-opacity': 0.35 }} />
                            <Layer id="activity-line" type="line" paint={{ 'line-color': selectedActivity.activity_type_color || '#E07A5F', 'line-width': 3 }} />
                          </Source>
                        ) : selectedActivity.area_points.length >= 2 ? (
                          <Source
                            id="activity-line-src"
                            type="geojson"
                            data={{
                              type: 'Feature',
                              geometry: {
                                type: 'LineString',
                                coordinates: selectedActivity.area_points,
                              },
                            }}
                          >
                            <Layer id="activity-path" type="line" paint={{ 'line-color': selectedActivity.activity_type_color || '#E07A5F', 'line-width': 3 }} />
                          </Source>
                        ) : null}

                        {/* Point markers */}
                        {selectedActivity.area_points.map((point, idx) => (
                          <Marker key={`detail-pt-${point[0]}-${point[1]}`} longitude={point[0]} latitude={point[1]}>
                            <div 
                              className="w-5 h-5 rounded-full border-2 border-white shadow-lg flex items-center justify-center text-white text-[10px] font-bold"
                              style={{ backgroundColor: selectedActivity.activity_type_color || '#E07A5F' }}
                            >
                              {idx + 1}
                            </div>
                          </Marker>
                        ))}
                      </Map>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: selectedActivity.activity_type_color || '#E07A5F', opacity: 0.5 }} />
                        Activity area
                      </span>
                      {selectedActivity.location_id && getLocationBoundary(selectedActivity.location_id) && (
                        <span className="flex items-center gap-1">
                          <span className="w-3 h-3 rounded-sm bg-[#1B4332] opacity-30" />
                          Field boundary
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Created by */}
                {selectedActivity.created_by && (
                  <p className="text-xs text-muted-foreground">Recorded by {selectedActivity.created_by}</p>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
